import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Popover from 'react-bootstrap/Popover';
import { useTranslation } from 'react-i18next';

import { MoreIcon } from '../../bombiix-icon';
import './CommonTabSwitch.scss';

export interface TabSwitchItem {
  eventKey: string;
  eventName: string;
  isDisabled?: boolean;
}

interface PillPosition {
  width: number;
  left: number;
  display?: 'none';
}

interface CommonTabSwitchProps {
  masterActiveTab: string;
  MenuList: TabSwitchItem[];
  isAPILoading?: boolean;
  activeTheTab: (eventKey: string) => void;
  underlineClassName?: string;
}

/** Width reserved for the MoreIcon button (20px glyph + surrounding gap/padding). */
const MORE_SLOT_WIDTH = 32;
/** Horizontal gap between tabs — mirrors the flex gap in CommonTabSwitch.scss. */
const TAB_GAP = 8;

/**
 * Segmented pill tab switch (Figma nodes 12358:160579 / 12948:715059). Renders
 * a rounded container of pill tabs with a sliding green pill behind the active
 * tab. Tabs that overflow the container width collapse into a MoreIcon popover.
 * Drop-in shape for the ProductCostingTabMenu `isNormalSwitch` usage.
 */
function CommonTabSwitch({
  masterActiveTab,
  MenuList,
  isAPILoading = false,
  activeTheTab,
  underlineClassName = '',
}: CommonTabSwitchProps) {
  const { t } = useTranslation('common');
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const measureRef = useRef<HTMLUListElement>(null);
  const [visibleCount, setVisibleCount] = useState(MenuList.length);
  const [pillPosition, setPillPosition] = useState<PillPosition>({ width: 0, left: 0 });

  const visibleTabs = useMemo(() => MenuList.slice(0, visibleCount), [MenuList, visibleCount]);
  const overflowTabs = useMemo(() => MenuList.slice(visibleCount), [MenuList, visibleCount]);

  /**
   * Decides how many leading tabs fit; the rest collapse into the MoreIcon
   * popover. The hidden measure list mirrors MenuList 1:1, so each child's width
   * is the tab's natural width regardless of the current visible/overflow split.
   * When at least one tab is hidden, room is reserved for the MoreIcon by
   * dropping further tabs until the icon fits alongside the visible ones.
   */
  const recalcOverflow = useCallback(() => {
    const container = containerRef.current;
    const measureList = measureRef.current;
    if (!container || !measureList || MenuList.length === 0) {
      return;
    }
    const tabWidths = Array.from(measureList.children).map((child) => child.getBoundingClientRect().width);
    const available = container.clientWidth;

    let used = 0;
    let fitCount = 0;
    for (let index = 0; index < tabWidths.length; index += 1) {
      const nextUsed = used + (tabWidths[index] ?? 0) + (index > 0 ? TAB_GAP : 0);
      if (nextUsed <= available) {
        used = nextUsed;
        fitCount = index + 1;
      } else {
        break;
      }
    }
    if (fitCount < tabWidths.length) {
      while (fitCount > 0 && used + MORE_SLOT_WIDTH > available) {
        used -= (tabWidths[fitCount - 1] ?? 0) + (fitCount > 1 ? TAB_GAP : 0);
        fitCount -= 1;
      }
    }
    setVisibleCount(Math.max(fitCount, 1));
  }, [MenuList]);

  useLayoutEffect(() => {
    recalcOverflow();
  }, [recalcOverflow]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver(() => recalcOverflow());
    observer.observe(container);
    return () => observer.disconnect();
  }, [recalcOverflow]);

  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list || !masterActiveTab) {
      setPillPosition({ width: 0, left: 0, display: 'none' });
      return;
    }
    const activeTab = list.querySelector<HTMLElement>('.common-tab-switch__item-wrap.active');
    if (activeTab) {
      const tabRect = activeTab.getBoundingClientRect();
      const parentRect = list.getBoundingClientRect();
      setPillPosition({ width: tabRect.width, left: tabRect.left - parentRect.left });
    } else {
      setPillPosition({ width: 0, left: 0, display: 'none' });
    }
  }, [masterActiveTab, visibleTabs]);

  useEffect(() => {
    if (!masterActiveTab && MenuList.length > 0) {
      const firstEnabledTab = MenuList.find((tab) => !tab.isDisabled);
      if (firstEnabledTab) {
        activeTheTab(firstEnabledTab.eventKey);
      }
    }
  }, [masterActiveTab, MenuList, activeTheTab]);

  if (!Array.isArray(MenuList) || MenuList.length === 0) {
    return null;
  }

  const overflowActive = overflowTabs.some((tab) => tab.eventKey === masterActiveTab);

  const overflowPopover = (
    <Popover id="common-tab-switch-overflow" className="common-tab-switch__popover">
      <Popover.Content>
        <ul className="common-tab-switch__overflow-list">
          {overflowTabs.map((element) => (
            <li key={element.eventKey}>
              <button
                type="button"
                className={`common-tab-switch__overflow-item ${masterActiveTab === element.eventKey ? 'active' : ''}`}
                title={t(element.eventName)}
                disabled={element.isDisabled || isAPILoading}
                onClick={() => activeTheTab(element.eventKey)}
              >
                {t(element.eventName)}
              </button>
            </li>
          ))}
        </ul>
      </Popover.Content>
    </Popover>
  );

  return (
    <div className="common-tab-switch" ref={containerRef}>
      {/* Hidden measurement pass — full list, natural widths, never painted. */}
      <ul className="common-tab-switch__list common-tab-switch__measure" ref={measureRef} aria-hidden>
        {MenuList.map((element) => (
          <li key={element.eventKey} className="common-tab-switch__item-wrap">
            <span className="common-tab-switch__item">{t(element.eventName)}</span>
          </li>
        ))}
      </ul>

      <ul className="common-tab-switch__list" ref={listRef}>
        {visibleTabs.map((element) => (
          <li key={element.eventKey} className={`common-tab-switch__item-wrap ${masterActiveTab === element.eventKey ? 'active' : ''}`}>
            <button
              type="button"
              className="common-tab-switch__item"
              title={t(element.eventName)}
              disabled={element.isDisabled || isAPILoading}
              onClick={() => activeTheTab(element.eventKey)}
            >
              {t(element.eventName)}
            </button>
          </li>
        ))}
        {overflowTabs.length > 0 && (
          <li className="common-tab-switch__more">
            <OverlayTrigger placement="bottom-end" trigger="click" rootClose overlay={overflowPopover}>
              <button
                type="button"
                className={`common-tab-switch__more-btn ${overflowActive ? 'active' : ''}`}
                aria-label={t('COMMON_TAB_SWITCH.MORE_TABS')}
              >
                <MoreIcon size={20} />
              </button>
            </OverlayTrigger>
          </li>
        )}
        <div className={`common-tab-switch__pill ${underlineClassName}`} style={pillPosition} />
      </ul>
    </div>
  );
}

export default CommonTabSwitch;
